export class TabElement extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', (event) => {
      const controller = event.target.closest('[aria-controls]');

      if (event.target.closest('[data-dialog-controller]')) return;

      this.handleClick(controller);
    });

    this.initializeHighlight();
    this.initializeScrollIndicators();
  }

  initializeHighlight() {
    this.highlight = this.querySelector('.tab-highlight');
    if (!this.highlight) return;

    const firstTab = this.querySelector('[aria-selected="true"]');
    if (firstTab) this.initHighlight(firstTab, this.highlight);
  }

  initHighlight(firstTab, highlight) {
    this.updateHighlight(firstTab);
    setTimeout(() => {
      highlight.classList.add('transition-all', 'duration-300');
    }, 250);
  }

  updateHighlight(activeTab) {
    if (!this.highlight || !activeTab) return;

    const tabList = this.highlight.parentElement;
    const { left, width, height } = activeTab.getBoundingClientRect();
    const containerLeft = tabList.getBoundingClientRect().left;
    const paddingLeft = parseInt(getComputedStyle(tabList).paddingLeft, 10);

    Object.assign(this.highlight.style, {
      width: `${width}px`,
      height: `${height}px`,
      transform: `translateX(${left - containerLeft + tabList.scrollLeft - paddingLeft}px)`,
    });
  }

  initializeScrollIndicators() {
    this.tabList = this.querySelector('[role="tablist"]');
    this.fadeOverlay = this.querySelector('.tab-fade-overlay');
    if (!this.tabList || !this.fadeOverlay) return;

    this.updateScrollIndicators();
    this.tabList.addEventListener('scroll', () => this.updateScrollIndicators());
    window.addEventListener('resize', () => this.updateScrollIndicators());
  }

  updateScrollIndicators() {
    if (!this.tabList || !this.fadeOverlay) return;

    const { scrollLeft, scrollWidth, clientWidth } = this.tabList;
    const hasOverflow = scrollWidth > clientWidth;
    const canScrollRight = hasOverflow && scrollLeft < scrollWidth - clientWidth - 1;

    if (canScrollRight) {
      this.fadeOverlay.classList.add('opacity-100');
      this.fadeOverlay.classList.remove('opacity-0');
    } else {
      this.fadeOverlay.classList.add('opacity-0');
      this.fadeOverlay.classList.remove('opacity-100');
    }
  }

  scrollTabIntoView(tab) {
    if (!this.tabList || !tab) return;

    const { scrollLeft, clientWidth } = this.tabList;
    const tabRect = tab.getBoundingClientRect();
    const containerRect = this.tabList.getBoundingClientRect();
    const tabLeft = tabRect.left - containerRect.left + scrollLeft;
    const tabCenter = tabLeft + tabRect.width / 2;
    const containerCenter = clientWidth / 2;

    // Calculate scroll position to center the tab
    const newScrollLeft = tabCenter - containerCenter;

    // Only scroll if there's a meaningful difference
    if (Math.abs(newScrollLeft - scrollLeft) > 5) {
      this.tabList.scrollTo({ left: newScrollLeft, behavior: 'smooth' });
    }
  }

  handleClick(controller) {
    const tabId = controller?.getAttribute('aria-controls');
    if (!tabId) return;

    this.tabControllers = this.tabControllers || this.querySelectorAll('[aria-controls]');
    this.tabPanels = this.tabPanels || this.querySelectorAll('[aria-labelledby]');

    this.tabControllers.forEach((controller) => {
      controller.ariaSelected = controller.getAttribute('aria-controls') === tabId;
    });

    this.tabPanels.forEach((tab) => {
      tab.ariaHidden = tab.id !== tabId;
    });

    // Update background images for tabs-on-image mode
    this.updateTabBackgrounds(tabId);

    this.scrollTabIntoView(controller);
    this.updateHighlight(controller);
    this.updateScrollIndicators();
  }

  updateTabBackgrounds(tabId) {
    // Extract block ID from tab ID (format: TabPanel-{blockId})
    const blockId = tabId.replace('TabPanel-', '');

    // Find the background images container (sibling of tab-element's parent container)
    const sectionWrapper = this.closest('.relative.overflow-hidden');
    if (!sectionWrapper) return;

    const backgrounds = sectionWrapper.querySelectorAll('[data-tab-background]');
    if (!backgrounds.length) return;

    for (const bg of backgrounds) {
      bg.ariaHidden = bg.dataset.tabBackground !== blockId;
    }
  }
}

customElements.define('tab-element', TabElement);
